import { useState, useEffect, useCallback } from "react"
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material"
import { ChevronLeft } from "@mui/icons-material"
import { useNavigate, useParams } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"

export default function EditSensor() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [formData, setFormData] = useState({
    name: "",
    sensor_type_id: "",
    imei: "",
    serial_number: "",
    latitude: "",
    longitude: "",
    is_active: false,
  })
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [sensorTypes, setSensorTypes] = useState([])

  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ALL)
      setSensorTypes(response.data.sensorTypes || [])
    } catch (err) {
      console.error("Error fetching sensor types:", err)
      setError("Failed to fetch sensor types. Please try again.")
    }
  }, [])

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(`${API_ENDPOINTS.SENSOR.GET_ONE(id)}`)
      const sensorData = response.data.sensor
      setFormData({
        name: sensorData.name,
        sensor_type_id: sensorData.sensor_type_id.id,
        imei: sensorData.imei,
        serial_number: sensorData.serial_number,
        latitude: sensorData.latitude || "",
        longitude: sensorData.longitude || "",
        is_active: sensorData.is_active,
        url: sensorData.url || "",
      })
    } catch (err) {
      console.error("Error fetching sensor:", err)
      setError("Failed to fetch sensor data. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await fetchRoles()
        await fetchUser()
      } catch (err) {
        console.error("Error fetching data:", err)
        setError("Failed to fetch data. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [fetchRoles, fetchUser])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const response = await api.put(`${API_ENDPOINTS.SENSOR.UPDATE(id)}`, formData)
      if (response.status === 200 || response.status === 202) {
        setSuccess(true)
        setTimeout(() => {
          navigate("/sensors")
        }, 1500)
      } else {
        throw new Error(response.data.message[0] || "Failed to update sensor. Please try again.")
      }
    } catch (err) {
      console.error("Error updating sensor:", err)
      setError(err.response?.data?.message[0] || "Failed to update sensor. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (event) => {
    const { name, value, checked } = event.target
    setFormData({
      ...formData,
      [name]: name === "is_active" ? checked : value,
    })
  }

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#1756B6",
      },
    },
  }

  const selectSx = {
    borderRadius: 1,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E0E0E0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1756B6",
    },
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 8 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate("/sensors")}
          sx={{
            mr: 2,
            color: "#6C757D",
            "&:hover": {
              backgroundColor: "rgba(108, 117, 125, 0.04)",
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Edit Sensor
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: "0 0 10px rgba(0,0,0,0.05)",
          backgroundColor: "background.primary",
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField required label="Name" name="name" value={formData.name} onChange={handleChange} sx={inputSx} />

            <FormControl required>
              <InputLabel>Sensor Type</InputLabel>
              <Select
                name="sensor_type_id"
                value={formData.sensor_type_id}
                onChange={handleChange}
                label="Sensor Type"
                sx={selectSx}
              >
                {sensorTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField required label="IMEI" name="imei" value={formData.imei} onChange={handleChange} sx={inputSx} />
            <TextField
              required
              label="Serial number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField label="Latitude" name="latitude" value={formData.latitude} onChange={handleChange} sx={inputSx} />
            <TextField label="Longitude" name="longitude" value={formData.longitude} onChange={handleChange} sx={inputSx} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            {/* <TextField label="URL" name="url" value={formData.url} onChange={handleChange} sx={inputSx} /> */}
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: "#E0E0E0",
                      "&.Mui-checked": {
                        color: "#1967D2",
                      },
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#E0E0E0 !important",
                      opacity: 0.3,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#1967D2 !important",
                      opacity: 0.3,
                    },
                  }}
                />
              }
              label={<Typography sx={{ color: "text.secondary" }}>Active Status</Typography>}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                bgcolor: "#1967D2",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": { bgcolor: "#1756B6" },
                minWidth: 100
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Update"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/sensors")}
              disabled={submitting}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: "100%" }}>
          Sensor updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}
